import { useEffect, useMemo, useState } from 'react';

import type { PostLike } from '~/hooks/useLoaders/usePostInsights';
import type { Admin } from '~/interfaces/admin';
import type { Subscription } from '~/interfaces/subscriptions';

import useLikedIds from './useLoaders/useLikedIds';

const getIsLikedFromProps = (likes: PostLike[], admin?: Admin, subscription?: Subscription): boolean => {
  if (likes.length === 0 || (!admin && !subscription)) return false;
  return likes
    .filter((like) => like.liked)
    .some((like) => like.user_id === admin?.id || like.subscription_id === subscription?.id);
};

interface Props {
  postId: string;
  likes: PostLike[];
  areLikesFetched: boolean;
  admin?: Admin;
  subscription?: Subscription;
}

const usePostLikeState = ({ postId, likes, areLikesFetched, admin, subscription }: Props) => {
  const { isPostLiked } = useLikedIds({ postId });

  // Derive server state
  const serverIsLiked = isPostLiked(postId) || (areLikesFetched && getIsLikedFromProps(likes, admin, subscription));
  const serverCount = useMemo(() => likes.filter((like) => like.liked).length, [likes]);

  // Optimistic state - persists until server state catches up
  const [optimistic, setOptimistic] = useState<{ liked: boolean; delta: number } | null>(null);

  // Clear optimistic state when server state matches it (i.e., server caught up)
  useEffect(() => {
    if (optimistic !== null && optimistic.liked === serverIsLiked) {
      setOptimistic(null);
    }
  }, [serverIsLiked, optimistic]);

  const toggle = () => {
    const nextLiked = !(optimistic?.liked ?? serverIsLiked);
    setOptimistic({ liked: nextLiked, delta: nextLiked ? 1 : -1 });
  };

  return {
    isLiked: optimistic?.liked ?? serverIsLiked,
    likesCount: Math.max(0, serverCount + (optimistic?.delta ?? 0)),
    toggle,
  };
};

export default usePostLikeState;
